import { useCallback, useEffect, useRef } from 'react'

import { Button } from 'antd'

import useDisclosure from 'src/hooks/use-disclosure'
import {
  renderActionItemAsButton,
  renderActionItemAsMenuItem,
} from 'src/hooks/use-make-action-item'
import { KeyboardKey } from 'src/types'
import { formatNumber } from 'src/utils/number'
import { getCurrentOS, OperatingSystem } from 'src/utils/window'

import { RowLevelAction } from '../../types'
import HStack from '../Flex/HStack'
import Icon from '../Icon/Icon'
import Menu from '../Menu/Menu'
import { T } from '../Text/Text'
import Toolbar from '../Toolbar/Toolbar'
import Tooltip from '../Tooltip/Tooltip'

type Props = {
  onClearSelections: () => void
  rowLevelActions: {
    primaryActions?: RowLevelAction[]
    secondaryActions?: RowLevelAction[]
  }
  selectedRowCount: number
}
const TableActionToolbar = ({ onClearSelections, rowLevelActions, selectedRowCount }: Props) => {
  const { isOpen, toggle, close } = useDisclosure()
  const actionButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClearSelections()
        e.preventDefault()
        e.stopPropagation()
      }
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        actionButtonRef.current?.click()
        e.preventDefault()
        e.stopPropagation()
      }
    }
    document.addEventListener('keydown', listener)
    return () => document.removeEventListener('keydown', listener)
  }, [onClearSelections])

  const handleButtonKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Space) {
        toggle()
      }
    },
    [toggle]
  )

  return (
    <>
      <Toolbar className="fixed bottom-8 left-[50%] flex translate-x-[-50%] gap-2 bg-white">
        <Tooltip content="Clear selections (Esc)">
          <Button type="dashed" onClick={onClearSelections}>
            <HStack gap={2} justify="center">
              <T.Body className="whitespace-nowrap">
                {formatNumber(selectedRowCount)} selected
              </T.Body>
              <Icon.FaXmark />
            </HStack>
          </Button>
        </Tooltip>
        <Toolbar.Separator className="hidden md:block" />
        {rowLevelActions?.primaryActions?.map(({ actionItem }) =>
          renderActionItemAsButton(actionItem, { size: 'middle' })
        )}
        {(rowLevelActions?.secondaryActions?.length ?? 0) > 0 && (
          <div>
            <Menu isOpen={isOpen} onClose={close}>
              <Menu.Trigger className="w-full">
                <div className="w-full">
                  <Tooltip
                    content={`${getCurrentOS() === OperatingSystem.Windows ? 'Ctrl' : '⌘'}+K Open actions menu`}
                    asChild
                  >
                    <Button
                      icon={<Icon.FaCommand />}
                      onClick={toggle}
                      onKeyDown={handleButtonKeyDown}
                      ref={actionButtonRef}
                      className="w-full"
                    >
                      Actions
                    </Button>
                  </Tooltip>
                </div>
              </Menu.Trigger>
              <Menu.Content align="end" side="bottom">
                {rowLevelActions.secondaryActions?.map(({ actionItem }) =>
                  renderActionItemAsMenuItem(actionItem)
                )}
              </Menu.Content>
            </Menu>
          </div>
        )}
      </Toolbar>
      {rowLevelActions?.primaryActions?.map(({ modal }) => modal)}
      {rowLevelActions?.secondaryActions?.map(({ modal }) => modal)}
    </>
  )
}

export default React.memo(TableActionToolbar)
